import type { ServiceInformationProps } from '@/modules/Service/types'
import { formatCurrency, formatDate } from '@/utils/function'
import { Briefcase, Plus, Trash2 } from 'lucide-react'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { useOtherStandardConfig } from './useOtherStandardConfig'
import { dateValue, formatPriceInput, parsePriceInput } from './utils'

const FieldBox = ({
    label,
    value,
    children,
    className = '',
}: {
    label: string
    value?: string | number
    children?: React.ReactNode
    className?: string
}) => (
    <div
        className={`relative rounded border border-gray-200 px-4 py-3 ${className}`}
    >
        <div className="absolute -top-2 left-3 bg-white px-1 text-xs text-gray-500">
            {label}
        </div>
        {children || (
            <div className="min-h-6 text-sm font-medium text-gray-900">
                {value || '-'}
            </div>
        )}
    </div>
)

const inputClassName =
    'h-8 w-full border-none bg-transparent p-0 text-sm font-medium text-gray-900 outline-none'

const OtherStandardConfig: React.FC<ServiceInformationProps> = ({
    service,
}) => {
    const { t } = useTranslation('services')
    const {
        canEdit,
        canSave,
        draft,
        renderState,
        setDraft,
        updateRange,
        updatePaymentMode,
        addRange,
        removeRange,
        save,
    } = useOtherStandardConfig(service)

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-4">
            <h3 className="text-base font-medium text-gray-900">
                {t('main-configuration')}
            </h3>

            <div className="overflow-hidden rounded border border-gray-200 bg-white">
                <div className="flex items-center gap-4 border-b border-gray-200 bg-gray-50 px-7 py-6">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded bg-blue-100 text-primary">
                        <Briefcase className="h-5 w-5" />
                    </span>
                    <div>
                        <div className="font-semibold text-gray-900">
                            {t('other_standard.payment_time_setup')}
                        </div>
                        <div className="text-sm text-gray-500">
                            {t('other_standard.payment_ranges')}
                        </div>
                    </div>
                </div>

                <div className="p-5">
                    <div className="rounded border border-gray-200 p-5">
                        <div className="mb-5 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-3 font-semibold text-gray-900">
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs text-primary">
                                    1
                                </span>
                                {service.service_name}
                            </div>
                            {canEdit && (
                                <Trash2 className="h-4 w-4 text-red-500" />
                            )}
                        </div>

                        <div className="space-y-5">
                            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                                <FieldBox
                                    label={t('other_standard.payment_method')}
                                >
                                    {canEdit ? (
                                        <select
                                            className={inputClassName}
                                            value={
                                                draft.payment_method ||
                                                'prepaid'
                                            }
                                            onChange={(event) =>
                                                setDraft((current) => ({
                                                    ...current,
                                                    payment_method:
                                                        event.target.value,
                                                }))
                                            }
                                        >
                                            <option value="prepaid">
                                                {t('other_standard.prepaid')}
                                            </option>
                                            <option value="postpaid">
                                                {t('other_standard.postpaid')}
                                            </option>
                                        </select>
                                    ) : (
                                        <div className="min-h-6 text-sm font-medium text-gray-900">
                                            {draft.payment_method
                                                ? t(
                                                      `other_standard.${draft.payment_method}`
                                                  )
                                                : '-'}
                                        </div>
                                    )}
                                </FieldBox>

                                <FieldBox
                                    label={t('other_standard.payment_mode')}
                                >
                                    {canEdit ? (
                                        <select
                                            className={inputClassName}
                                            value={
                                                draft.payment_mode || 'one_time'
                                            }
                                            onChange={(event) =>
                                                updatePaymentMode(
                                                    event.target.value
                                                )
                                            }
                                        >
                                            <option value="one_time">
                                                {t('other_standard.one_time')}
                                            </option>
                                            <option value="multiple_times">
                                                {t(
                                                    'other_standard.multiple_times'
                                                )}
                                            </option>
                                        </select>
                                    ) : (
                                        <div className="min-h-6 text-sm font-medium text-gray-900">
                                            {t(
                                                `other_standard.${draft.payment_mode || 'one_time'}`
                                            )}
                                        </div>
                                    )}
                                </FieldBox>

                                <FieldBox label={t('description')}>
                                    {canEdit ? (
                                        <textarea
                                            className={`${inputClassName} min-h-8 resize-y`}
                                            value={draft.description || ''}
                                            onChange={(event) =>
                                                setDraft((current) => ({
                                                    ...current,
                                                    description:
                                                        event.target.value,
                                                }))
                                            }
                                        />
                                    ) : (
                                        <div className="min-h-6 whitespace-pre-wrap text-sm font-medium text-gray-900">
                                            {draft.description || '-'}
                                        </div>
                                    )}
                                </FieldBox>
                            </div>

                            <div className="space-y-3">
                                <div className="text-sm font-medium text-gray-500">
                                    {t('other_standard.payment_ranges')}
                                </div>

                                {(draft.ranges || []).map((range, index) => (
                                    <div
                                        key={index}
                                        className="rounded border border-gray-100 bg-gray-50 p-4"
                                    >
                                        <div className="mb-4 flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-3 text-sm font-semibold text-gray-900">
                                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs text-primary">
                                                    {index + 1}
                                                </span>
                                                {t(
                                                    'other_standard.payment_range',
                                                    {
                                                        number: index + 1,
                                                    }
                                                )}
                                            </div>
                                            {canEdit &&
                                                draft.payment_mode ===
                                                    'multiple_times' &&
                                                (draft.ranges?.length || 0) >
                                                    1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            removeRange(index)
                                                        }
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded text-red-500 hover:bg-red-50"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                        </div>

                                        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                                            <FieldBox
                                                label={t(
                                                    'other_standard.start_date'
                                                )}
                                            >
                                                {canEdit ? (
                                                    <input
                                                        type="date"
                                                        className={
                                                            inputClassName
                                                        }
                                                        value={dateValue(
                                                            range.start_date
                                                        )}
                                                        onChange={(event) =>
                                                            updateRange(
                                                                index,
                                                                'start_date',
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                    />
                                                ) : (
                                                    <div className="min-h-6 text-sm font-medium text-gray-900">
                                                        {formatDate(
                                                            range.start_date ||
                                                                null
                                                        )}
                                                    </div>
                                                )}
                                            </FieldBox>

                                            <FieldBox
                                                label={t(
                                                    'other_standard.end_date'
                                                )}
                                            >
                                                {canEdit ? (
                                                    <input
                                                        type="date"
                                                        className={
                                                            inputClassName
                                                        }
                                                        value={dateValue(
                                                            range.end_date
                                                        )}
                                                        onChange={(event) =>
                                                            updateRange(
                                                                index,
                                                                'end_date',
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                    />
                                                ) : (
                                                    <div className="min-h-6 text-sm font-medium text-gray-900">
                                                        {formatDate(
                                                            range.end_date ||
                                                                null
                                                        )}
                                                    </div>
                                                )}
                                            </FieldBox>

                                            <FieldBox
                                                label={t(
                                                    'other_standard.price'
                                                )}
                                            >
                                                {canEdit ? (
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        className={
                                                            inputClassName
                                                        }
                                                        value={formatPriceInput(
                                                            Number(
                                                                range.price || 0
                                                            )
                                                        )}
                                                        onChange={(event) =>
                                                            updateRange(
                                                                index,
                                                                'price',
                                                                parsePriceInput(
                                                                    event.target
                                                                        .value
                                                                )
                                                            )
                                                        }
                                                    />
                                                ) : (
                                                    <div className="min-h-6 text-sm font-medium text-gray-900">
                                                        {formatCurrency(
                                                            Number(
                                                                range.price || 0
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                            </FieldBox>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center justify-between gap-3">
                                {canEdit &&
                                draft.payment_mode === 'multiple_times' ? (
                                    <button
                                        type="button"
                                        onClick={addRange}
                                        className="inline-flex items-center gap-2 rounded border border-primary px-4 py-2 text-sm font-medium text-primary"
                                    >
                                        <Plus className="h-4 w-4" />
                                        {t('other_standard.add_payment_range')}
                                    </button>
                                ) : (
                                    <span />
                                )}

                                <button
                                    type="button"
                                    onClick={save}
                                    disabled={!canSave}
                                    className={`inline-flex min-w-36 items-center justify-center rounded px-8 py-3 text-sm font-semibold ${
                                        canSave
                                            ? 'bg-primary text-white'
                                            : 'cursor-not-allowed bg-gray-200 text-gray-400'
                                    }`}
                                >
                                    Lưu
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default OtherStandardConfig
